/**
 * calculators-data.ts — Metadata registry for all 31 calculators.
 *
 * This file defines UI metadata: names, descriptions, icons, categories.
 * No formula logic lives here — formulas are in lib/formulas.ts.
 *
 * To add a new calculator: add an entry here + create the page + add formulas.
 */

export type CalculatorCategory = "diy" | "high-value";

export interface CalculatorMeta {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  description: string;
  category: CalculatorCategory;
  icon: string;          // Icon key matching components/Icon.tsx
  accentColor: string;   // Dynamic accent color
  keywords: string[];    // SEO keywords
  popular?: boolean;     // flag for "featured" badge
  comingSoon?: boolean;  // flag for Group B (not built yet)
  lastModified: string;  // Last modification date for sitemap
}

export const CALCULATORS: CalculatorMeta[] = [
  // ── Group A: DIY / High Volume ─────────────────────────
  {
    id: "concrete",
    slug: "concrete",
    name: "Concrete Calculator",
    shortName: "Concrete",
    description: "Calculate cubic yards and bags of concrete needed for slabs, footings, and columns.",
    category: "diy",
    icon: "concrete",
    accentColor: "#0284c7", // Sky blue
    keywords: ["concrete calculator", "cubic yards", "concrete bags", "how much concrete", "slab calculator"],
    popular: true,
    lastModified: "2026-07-14",
  },
  {
    id: "paint",
    slug: "paint",
    name: "Paint Calculator",
    shortName: "Paint",
    description: "Find out how many gallons of paint you need for any room, including walls and ceilings.",
    category: "diy",
    icon: "paint",
    accentColor: "#ec4899", // Pink
    keywords: ["paint calculator", "how many gallons of paint", "paint coverage", "room paint estimator"],
    popular: true,
    lastModified: "2026-07-14",
  },
  {
    id: "flooring",
    slug: "flooring",
    name: "Flooring Calculator",
    shortName: "Flooring",
    description: "Estimate square footage and boxes of flooring needed with the right waste factor for your pattern.",
    category: "diy",
    icon: "flooring",
    accentColor: "#d97706", // Amber
    keywords: ["flooring calculator", "square footage", "hardwood flooring", "LVP calculator", "tile flooring"],
    lastModified: "2026-07-14",
  },
  {
    id: "area",
    slug: "area",
    name: "Interactive Square Footage Calculator: Room & Area Finder",
    shortName: "Area",
    description: "Use our free interactive square footage calculator to find the area of any shape in square feet, meters, or yards instantly. Perfect for room dimensions.",
    category: "diy",
    icon: "area",
    accentColor: "#0d9488", // Teal
    keywords: ["square footage calculator", "area calculator", "square meters to square feet", "room size"],
    popular: true,
    lastModified: "2026-07-14",
  },
  {
    id: "fence",
    slug: "fence",
    name: "Fence Calculator",
    shortName: "Fence",
    description: "Calculate posts, rails, and pickets needed to build a wood or privacy fence.",
    category: "diy",
    icon: "fence",
    accentColor: "#65a30d", // Lime
    keywords: ["fence calculator", "fence materials", "how many fence posts", "picket fence calculator"],
    lastModified: "2026-07-14",
  },
  {
    id: "mulch",
    slug: "mulch",
    name: "Mulch & Gravel Calculator",
    shortName: "Mulch / Gravel",
    description: "Calculate cubic yards or bags of mulch, gravel, or topsoil needed for your landscape.",
    category: "diy",
    icon: "mulch",
    accentColor: "#16a34a", // Green
    keywords: ["mulch calculator", "gravel calculator", "cubic yards of mulch", "landscaping calculator"],
    lastModified: "2026-07-14",
  },
  {
    id: "drywall",
    slug: "drywall",
    name: "Drywall Calculator",
    shortName: "Drywall",
    description: "Figure out how many 4×8 drywall sheets you need for walls and ceilings, with waste included.",
    category: "diy",
    icon: "drywall",
    accentColor: "#475569", // Slate
    keywords: ["drywall calculator", "sheetrock calculator", "how many drywall sheets", "drywall estimator"],
    lastModified: "2026-07-14",
  },
  {
    id: "tile",
    slug: "tile",
    name: "Tile Calculator",
    shortName: "Tile",
    description: "Calculate tiles and grout needed for floors or walls, with pattern-specific waste factors.",
    category: "diy",
    icon: "tile",
    accentColor: "#0ea5e9", // Sky
    keywords: ["tile calculator", "how many tiles", "tile estimator", "grout calculator", "floor tile"],
    lastModified: "2026-07-14",
  },

  // ── Group B: High-Value / Cost Estimators ───────────────
  {
    id: "solar",
    slug: "solar",
    name: "Solar Savings Calculator",
    shortName: "Solar Savings",
    description: "Estimate how many solar panels you need and how much you could save on your energy bill.",
    category: "high-value",
    icon: "solar",
    accentColor: "#ea580c", // Orange
    keywords: ["solar calculator", "solar panel savings", "solar energy cost", "how many solar panels"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "hvac",
    slug: "hvac",
    name: "HVAC Replacement Cost",
    shortName: "HVAC Cost",
    description: "Estimate the cost of replacing your HVAC system based on home size and system type.",
    category: "high-value",
    icon: "hvac",
    accentColor: "#06b6d4", // Cyan
    keywords: ["HVAC cost calculator", "AC replacement cost", "HVAC system price", "central air cost"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "roofing",
    slug: "roofing",
    name: "Roof Replacement Cost",
    shortName: "Roofing Cost",
    description: "Get a roof replacement cost estimate based on home size, pitch, and roofing material.",
    category: "high-value",
    icon: "roofing",
    accentColor: "#e11d48", // Rose/Red
    keywords: ["roofing calculator", "roof replacement cost", "cost to replace roof", "shingle cost"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "kitchen",
    slug: "kitchen",
    name: "Interactive Kitchen Remodel Cost Calculator",
    shortName: "Kitchen Remodel",
    description: "Estimate your kitchen remodel budget instantly by scope, layout changes, and size based on updated 2026 national cost data.",
    category: "high-value",
    icon: "kitchen",
    accentColor: "#ea580c", // Orange
    keywords: ["kitchen remodel cost", "kitchen renovation calculator", "how much to remodel kitchen"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "bathroom",
    slug: "bathroom",
    name: "Interactive Bathroom Remodel Cost Calculator",
    shortName: "Bathroom Remodel",
    description: "Estimate bathroom renovation costs from a simple budget refresh to a luxury master bath remodel using our interactive estimator.",
    category: "high-value",
    icon: "bathroom",
    accentColor: "#8b5cf6", // Violet/Purple
    keywords: ["bathroom remodel cost", "bathroom renovation calculator", "how much to remodel bathroom"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "windows",
    slug: "windows",
    name: "Window Replacement Cost",
    shortName: "Windows",
    description: "Estimate window replacement costs by window type, frame material, and glass package.",
    category: "high-value",
    icon: "windows",
    accentColor: "#0891b2", // Cyan/Teal
    keywords: ["window replacement cost", "cost to replace windows", "window calculator", "window installation cost"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "deck",
    slug: "deck",
    name: "Interactive Deck Cost Calculator: Build & Material Estimator",
    shortName: "Deck Cost",
    description: "Calculate your wood or composite deck installation cost. Estimate materials, frames, steps, and labor costs instantly.",
    category: "high-value",
    icon: "deck",
    accentColor: "#22c55e", // Green
    keywords: ["deck cost calculator", "cost to build a deck", "deck installation cost", "composite deck cost"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "roi",
    slug: "roi",
    name: "Remodeling ROI Calculator",
    shortName: "Remodel ROI",
    description: "See how much value a home improvement project adds at resale, based on NAR 2025 data.",
    category: "high-value",
    icon: "roi",
    accentColor: "#10b981", // Emerald
    keywords: ["remodeling ROI", "home improvement ROI", "kitchen ROI", "bathroom ROI", "home value added"],
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "water-heater",
    slug: "water-heater",
    name: "Water Heater Replacement Cost",
    shortName: "Water Heater",
    description: "Estimate urgent water heater replacement costs by fuel type, capacity, emergency timing, and code-related upgrades.",
    category: "high-value",
    icon: "water-heater",
    accentColor: "#0ea5e9",
    keywords: ["water heater replacement cost", "water heater cost calculator", "tankless water heater cost", "heat pump water heater cost"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "roof-shingles",
    slug: "roof-shingles",
    name: "Roof Shingle & Bundle Calculator",
    shortName: "Shingle Bundles",
    description: "Calculate roofing squares, shingle bundles, starter, ridge cap, and underlayment rolls for asphalt roof projects.",
    category: "diy",
    icon: "roof-shingles",
    accentColor: "#dc2626",
    keywords: ["roofing calculator", "roof shingle calculator", "shingle bundle calculator", "how many bundles of shingles"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "siding",
    slug: "siding",
    name: "Siding Cost Calculator",
    shortName: "Siding Cost",
    description: "Estimate siding replacement cost by wall area, material, stories, removal, house wrap, and trim openings.",
    category: "high-value",
    icon: "siding",
    accentColor: "#14b8a6",
    keywords: ["siding cost calculator", "vinyl siding cost", "fiber cement siding cost", "house siding estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "driveway",
    slug: "driveway",
    name: "Driveway / Paving Cost Calculator",
    shortName: "Driveway Cost",
    description: "Estimate driveway paving costs for asphalt, concrete, gravel, or pavers with demolition and base prep.",
    category: "high-value",
    icon: "driveway",
    accentColor: "#64748b",
    keywords: ["driveway cost calculator", "asphalt driveway cost", "concrete driveway cost", "paving calculator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "home-addition",
    slug: "home-addition",
    name: "Home Addition Cost Calculator",
    shortName: "Home Addition",
    description: "Estimate room addition costs by square footage, room type, finish level, foundation, design, and permits.",
    category: "high-value",
    icon: "home-addition",
    accentColor: "#7c3aed",
    keywords: ["home addition cost calculator", "room addition cost", "cost to add a room", "house addition estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "pool",
    slug: "pool",
    name: "Pool Cost Calculator",
    shortName: "Pool Cost",
    description: "Estimate above-ground, vinyl-liner, fiberglass, or gunite pool costs with site work, heater, salt system, and decking.",
    category: "high-value",
    icon: "pool",
    accentColor: "#0ea5e9",
    keywords: ["pool cost calculator", "inground pool cost", "above ground pool cost", "swimming pool estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "countertop",
    slug: "countertop",
    name: "Countertop Replacement Cost Calculator",
    shortName: "Countertops",
    description: "Estimate countertop replacement cost by square footage, material, edge profile, cutouts, removal, and backsplash.",
    category: "high-value",
    icon: "countertop",
    accentColor: "#a855f7",
    keywords: ["countertop cost calculator", "quartz countertop cost", "granite countertop cost", "countertop replacement estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "gutters",
    slug: "gutters",
    name: "Gutter Installation Cost Calculator",
    shortName: "Gutters",
    description: "Estimate gutter installation cost by linear feet, material, home height, downspouts, guards, removal, and fascia repair.",
    category: "high-value",
    icon: "gutters",
    accentColor: "#0891b2",
    keywords: ["gutter installation cost", "gutter cost calculator", "seamless gutter cost", "gutter replacement estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "basement-finishing",
    slug: "basement-finishing",
    name: "Basement Finishing Cost Calculator",
    shortName: "Basement Finish",
    description: "Estimate basement finishing cost by square footage, finish level, bathroom, egress windows, moisture prep, and permits.",
    category: "high-value",
    icon: "basement",
    accentColor: "#475569",
    keywords: ["basement finishing cost", "basement remodel calculator", "finished basement cost", "basement conversion estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "insulation",
    slug: "insulation",
    name: "Insulation Calculator",
    shortName: "Insulation",
    description: "Calculate added R-value, insulation depth, order area, and installed cost by DOE climate-zone targets.",
    category: "diy",
    icon: "insulation",
    accentColor: "#f59e0b",
    keywords: ["insulation calculator", "attic insulation calculator", "R value calculator", "how much insulation do I need"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "cabinet-refacing",
    slug: "cabinet-refacing",
    name: "Cabinet Refacing / Replacement Cost Calculator",
    shortName: "Cabinets",
    description: "Estimate cabinet refacing, painting, or replacement cost by linear feet, doors, hardware, demolition, and layout changes.",
    category: "high-value",
    icon: "cabinet",
    accentColor: "#b45309",
    keywords: ["cabinet refacing cost", "cabinet replacement cost", "kitchen cabinet calculator", "cabinet installation estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "backup-generator",
    slug: "backup-generator",
    name: "Backup Generator Sizing & Cost Calculator",
    shortName: "Generator",
    description: "Size a home backup generator by essential loads and estimate portable, inverter, or standby generator installation cost.",
    category: "high-value",
    icon: "generator",
    accentColor: "#f97316",
    keywords: ["backup generator sizing calculator", "home generator cost", "standby generator calculator", "portable generator watts"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "ev-charger",
    slug: "ev-charger",
    name: "EV Charger Installation Cost Calculator",
    shortName: "EV Charger",
    description: "Estimate Level 2 EV charger installation cost by charger type, wire run, panel work, permitting, and trenching.",
    category: "high-value",
    icon: "ev-charger",
    accentColor: "#2563eb",
    keywords: ["EV charger installation cost", "Level 2 charger cost", "home EV charger calculator", "240 volt charger installation"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "siding-material",
    slug: "siding-material",
    name: "Siding Material Calculator",
    shortName: "Siding Material",
    description: "Calculate siding order quantity, cartons or boards, house wrap rolls, fasteners, trim, and waste factor.",
    category: "diy",
    icon: "siding",
    accentColor: "#0f766e",
    keywords: ["siding material calculator", "how much siding do I need", "vinyl siding calculator", "siding square footage"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
  {
    id: "retaining-wall",
    slug: "retaining-wall",
    name: "Retaining Wall Calculator",
    shortName: "Retaining Wall",
    description: "Estimate retaining wall blocks, face square footage, drainage, geogrid, excavation, engineering, and installed cost.",
    category: "high-value",
    icon: "retaining-wall",
    accentColor: "#78716c",
    keywords: ["retaining wall calculator", "retaining wall cost", "retaining wall blocks calculator", "landscape wall estimator"],
    popular: true,
    comingSoon: false,
    lastModified: "2026-07-14",
  },
];

/** Helper to get a calculator by slug */
export function getCalculator(slug: string): CalculatorMeta | undefined {
  return CALCULATORS.find((c) => c.slug === slug);
}

/** All DIY calculators */
export const DIY_CALCULATORS = CALCULATORS.filter((c) => c.category === "diy");

/** All high-value cost estimators */
export const HIGHVALUE_CALCULATORS = CALCULATORS.filter((c) => c.category === "high-value");
